import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatCard, StatusBadge } from "@/components/common";
import { numberID, formatDateTime, rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Factory, Printer, PackagePlus, PackageMinus, Boxes, AlertTriangle, PackageX, Pencil, Search, X, Shirt, Send, Trash2, Coins, Save, Info, Layers } from "lucide-react";
import { CwStockCell, CwMinimumCell, CwItemDetailDialog, fmtKg } from "./CatchWeightDetail";

const META = {
  KONVEKSI: { title: "Inventory Konveksi", subtitle: "Bahan baku SPH Factory — sistem stok terpisah dari Inventory Pusat.", icon: Factory,
    stockLabel: "Stok Bahan",
    categories: ["Kain", "Rib", "Benang", "Label", "Jarum", "Aksesoris Produksi", "Packaging Produksi"] },
  PRINTING: { title: "Inventory Digital Printing", subtitle: "Konsumabel digital printing — sistem stok terpisah dari inventory lain.", icon: Printer,
    stockLabel: "Stok Bahan",
    categories: ["DTF Ink", "DTF Film", "DTF Powder", "Sublimation Ink", "Sublimation Paper", "Sticker Material", "Banner Material", "Cleaning Solution", "Consumables"] },
};

const TXN_TYPES = [
  ["IN", "Barang Masuk"], ["OUT", "Barang Keluar"],
  ["TRANSFER_IN", "Transfer Masuk"], ["TRANSFER_OUT", "Transfer Keluar"],
  ["ADJUSTMENT", "Penyesuaian"], ["STOCK_OPNAME", "Stock Opname"],
  ["MATERIAL_USAGE", "Pemakaian Bahan"], ["RETURN_IN", "Retur Masuk"],
  ["PURCHASE_RECEIPT", "Penerimaan Pembelian"], ["PRODUCTION_USAGE", "Pemakaian Produksi"],
];
const TXN_LABEL = Object.fromEntries([...TXN_TYPES, ["RETURN_DAMAGED", "Retur Rusak"],
  ["SHIP", "Pengiriman"], ["RESERVE", "Reservasi"], ["RELEASE", "Rilis Reservasi"],
  ["PRODUCTION_FINISHED_IN", "Barang Jadi Produksi Masuk"]]);

function itemStatus(cur, min) {
  if (cur <= 0) return "OUT";
  if (min > 0 && cur <= min) return "LOW";
  return "OK";
}
const STATUS_LABEL = { OUT: "Stok Habis", LOW: "Stok Menipis", OK: "Normal" };

export default function InventoryMaterials({ type }) {
  const { can } = useAuth();
  const meta = META[type];
  const [wh, setWh] = useState(null);
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [openItem, setOpenItem] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [move, setMove] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [units, setUnits] = useState([]);
  const canTransact = can("inventory.transact");
  const canDelete = can("inventory.master.delete");
  const [showInactive, setShowInactive] = useState(false);
  const [cwEnabled, setCwEnabled] = useState(false);
  const [cwSummaries, setCwSummaries] = useState({});
  const [cwDetail, setCwDetail] = useState(null);

  useEffect(() => { api.get("/masters/units").then((r) => setUnits(r.data.filter((u) => u.status !== "inactive"))).catch(() => {}); }, []);
  useEffect(() => { api.get("/purchasing/catch-weight/config").then((r) => setCwEnabled(!!r.data.fabric_catch_weight_enabled)).catch(() => setCwEnabled(false)); }, []);

  useEffect(() => { api.get("/inventory/warehouses").then((r) => setWh(r.data.find((w) => w.type === type))); }, [type]);

  const load = () => {
    if (!wh) return;
    setItems(null);
    setCwSummaries({});
    api.get("/inventory/items", { params: { warehouse_id: wh.id } }).then((r) => {
      setItems(r.data);
      const cwIds = cwEnabled ? r.data.filter((it) => it.tracking_mode === "CATCH_WEIGHT_ROLL").map((it) => it.id) : [];
      if (cwIds.length) {
        api.get("/purchasing/fabric-rolls/summary-batch", { params: { inventory_item_ids: cwIds.join(","), warehouse_id: wh.id } })
          .then((rr) => setCwSummaries(rr.data.items || {})).catch(() => {});
      }
    });
    api.get("/inventory/materials-summary", { params: { warehouse_id: wh.id } }).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(load, [wh, cwEnabled]);

  const cwItems = cwEnabled ? Object.keys(cwSummaries) : [];
  const cwTotals = cwItems.reduce((acc, id) => {
    const s = cwSummaries[id];
    acc.rolls += s?.total_roll_count || 0;
    acc.usableRolls += s?.usable_roll_count || 0;
    acc.usableKg += s?.usable_weight_kg || 0;
    return acc;
  }, { rolls: 0, usableRolls: 0, usableKg: 0 });

  return (
    <div data-testid={`inv-${type}-page`}>
      <PageHeader title={meta.title} subtitle={meta.subtitle} testId={`inv-${type}-header`}>
        {canTransact && (
          <Button variant="outline" onClick={() => setOpenItem(true)} data-testid="add-item-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Item Baru
          </Button>
        )}
      </PageHeader>

      <h3 className="mb-3 font-display text-base font-semibold text-slate-700">Bahan Baku</h3>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 mb-4">
        <StatCard label="Jumlah Item" value={numberID(summary?.item_count)} icon={Boxes} accent="slate" testId={`${type}-item-count`} />
        <StatCard label="Stok Menipis" value={numberID(summary?.low_stock)} icon={AlertTriangle} accent="amber" testId={`${type}-low`} />
        <StatCard label="Stok Habis" value={numberID(summary?.out_of_stock)} icon={PackageX} accent="rose" testId={`${type}-out`} />
        <StatCard label="Jenis Satuan" value={numberID(summary?.units?.length)} icon={meta.icon} accent="indigo" hint="Total tidak digabung antar satuan" testId={`${type}-units`} />
      </div>

      {summary?.units?.length > 0 && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Total Stok per Satuan</p>
          <div className="flex flex-wrap gap-3">
            {summary.units.map((u) => (
              <div key={u.unit} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2" data-testid={`unit-${u.unit}`}>
                <p className="font-display text-lg font-bold text-slate-900">{numberID(u.total_qty)} <span className="text-sm font-medium text-slate-500">{u.unit}</span></p>
                <p className="text-[11px] text-slate-400">{numberID(u.item_count)} item</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {cwItems.length > 0 && (
        <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 shadow-sm" data-testid="cw-summary-strip">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-500"><Layers className="h-3.5 w-3.5" /> Kain Catch Weight <span className="font-normal normal-case text-slate-400">· Roll &amp; KG tidak digabung</span></p>
          <div className="flex flex-wrap gap-3">
            <div className="rounded-lg border border-indigo-200 bg-white px-4 py-2" data-testid="cw-total-usable-roll">
              <p className="font-display text-lg font-bold text-indigo-600">{numberID(cwTotals.usableRolls)} <span className="text-sm font-medium text-slate-500">Roll</span></p>
              <p className="text-[11px] text-slate-400">Roll Tersedia Saat Ini</p>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-white px-4 py-2" data-testid="cw-total-usable">
              <p className="font-display text-lg font-bold text-indigo-600">{fmtKg(cwTotals.usableKg)} <span className="text-sm font-medium text-slate-500">KG</span></p>
              <p className="text-[11px] text-slate-400">Usable KG Saat Ini</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2" data-testid="cw-total-roll">
              <p className="font-display text-lg font-bold text-slate-500">{numberID(cwTotals.rolls)} <span className="text-sm font-medium text-slate-400">Roll</span></p>
              <p className="text-[11px] text-slate-400">Roll Tercatat (Semua Status)</p>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="stok">
        <TabsList>
          <TabsTrigger value="stok" data-testid="tab-stok">{meta.stockLabel}</TabsTrigger>
          {type === "KONVEKSI" && <TabsTrigger value="finished" data-testid="tab-finished">Barang Jadi Produksi</TabsTrigger>}
          <TabsTrigger value="riwayat" data-testid="tab-riwayat">Riwayat Stok</TabsTrigger>
          <TabsTrigger value="minimum" data-testid="tab-minimum">Minimum Stok</TabsTrigger>
          {can("inventory.cost.view") && <TabsTrigger value="cost" data-testid="tab-cost">Cost &amp; Nilai</TabsTrigger>}
        </TabsList>

        <TabsContent value="stok" className="mt-4">
          <StokBahanTab items={items} meta={meta} canTransact={canTransact} canDelete={canDelete}
            showInactive={showInactive} onToggleInactive={setShowInactive}
            cwEnabled={cwEnabled} cwSummaries={cwSummaries} onOpenCw={(it) => setCwDetail(it)}
            onMove={(m) => setMove(m)} onEdit={(it) => setEditItem(it)} onDelete={(it) => setDelItem(it)} />
        </TabsContent>
        {type === "KONVEKSI" && (
          <TabsContent value="finished" className="mt-4">
            <FinishedGoodsTab wh={wh} canTransact={canTransact} />
          </TabsContent>
        )}
        <TabsContent value="riwayat" className="mt-4">
          <RiwayatTab wh={wh} items={items} meta={meta} />
        </TabsContent>
        <TabsContent value="minimum" className="mt-4">
          <MinimumTab items={items} canTransact={canTransact} onEdit={(it) => setEditItem(it)} />
        </TabsContent>
        {can("inventory.cost.view") && (
          <TabsContent value="cost" className="mt-4">
            <CostTab wh={wh} canManage={can("inventory.cost.manage")} />
          </TabsContent>
        )}
      </Tabs>

      {openItem && wh && <ItemDialog wh={wh} categories={meta.categories} units={units} cwEnabled={cwEnabled} onClose={() => setOpenItem(false)} onSaved={() => { setOpenItem(false); load(); }} />}
      {editItem && wh && <ItemDialog wh={wh} categories={meta.categories} units={units} cwEnabled={cwEnabled} item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(); }} />}
      {move && wh && <MoveDialog move={move} wh={wh} onClose={() => setMove(null)} onSaved={() => { setMove(null); load(); }} />}
      {delItem && <DeleteItemDialog item={delItem} onClose={() => setDelItem(null)} onSaved={() => { setDelItem(null); load(); }} />}
      {cwDetail && wh && <CwItemDetailDialog item={cwDetail} warehouseId={wh.id} onClose={() => setCwDetail(null)} />}
    </div>
  );
}

function DeleteItemDialog({ item, onClose, onSaved }) {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/inventory/items/${item.id}/delete-check`).then((r) => setCheck(r.data)).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [item.id]);
  const doDelete = async () => {
    setBusy(true);
    try { const r = await api.delete(`/inventory/items/${item.id}`); toast.success(`Item ${r.data.deleted_name} dihapus permanen`); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const deactivate = async () => {
    setBusy(true);
    try { await api.patch(`/inventory/items/${item.id}`, { status: "inactive" }); toast.success(`Item ${item.name} dinonaktifkan`); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="delete-item-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" /> Hapus Item — {item.name}</DialogTitle></DialogHeader>
        {!check ? <Loading /> : check.deletable ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-sm text-rose-800" data-testid="delete-item-allowed">
            <p>Item <b>{check.name}</b> belum pernah digunakan dalam transaksi. Tindakan ini <b>permanen</b> dan tidak dapat dibatalkan.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800" data-testid="delete-item-blocked">
            <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Item tidak dapat dihapus karena:</p>
            <ul className="ml-5 mt-1 list-disc space-y-0.5">{check.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
            <p className="mt-2">Nonaktifkan item sebagai gantinya agar riwayat transaksi tetap utuh.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          {check && !check.deletable && <Button onClick={deactivate} disabled={busy} data-testid="deactivate-item-btn" className="bg-amber-600 hover:bg-amber-500">Nonaktifkan</Button>}
          {check && check.deletable && <Button onClick={doDelete} disabled={busy} data-testid="confirm-delete-item-btn" className="bg-rose-600 hover:bg-rose-500">{busy ? "Menghapus..." : "Hapus Permanen"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StokBahanTab({ items, meta, canTransact, canDelete, showInactive, onToggleInactive, cwEnabled, cwSummaries, onOpenCw, onMove, onEdit, onDelete }) {
  if (!items) return <Loading />;
  const showActions = canTransact || canDelete;
  const visible = showInactive ? items : items.filter((it) => it.status !== "inactive");
  const emptyAll = items.length === 0;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <label className="flex items-center gap-2 text-sm text-slate-600" data-testid="toggle-show-inactive-item">
          <input type="checkbox" checked={showInactive} onChange={(e) => onToggleInactive(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-indigo-600" />
          Tampilkan Nonaktif
        </label>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
      {visible.length === 0 ? (
        emptyAll
          ? <EmptyState title="Belum ada item" subtitle="Tambahkan item lalu catat barang masuk." icon={meta.icon} />
          : <EmptyState title="Tidak ada item aktif" subtitle="Aktifkan 'Tampilkan Nonaktif' untuk melihat item nonaktif." icon={meta.icon} />
      ) : (
      <Table>
        <TableHeader><TableRow>
          <TableHead>Nama Item</TableHead><TableHead>Kode</TableHead><TableHead>Kategori</TableHead><TableHead>Satuan</TableHead>
          <TableHead className="text-right">Stok Saat Ini</TableHead><TableHead className="text-right">Minimum</TableHead>
          <TableHead>Status</TableHead><TableHead>Lokasi</TableHead>{showActions && <TableHead className="text-right">Aksi</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {visible.map((it) => {
            const st = itemStatus(it.on_hand, it.minimum_stock || 0);
            const isCw = cwEnabled && it.tracking_mode === "CATCH_WEIGHT_ROLL";
            return (
              <TableRow key={it.id} data-testid={`item-row-${it.id}`} className={it.status === "inactive" ? "opacity-60" : ""}>
                <TableCell className="font-medium">
                  {isCw ? (
                    <button className="text-left font-medium text-indigo-700 hover:underline" onClick={() => onOpenCw(it)} data-testid={`cw-open-${it.id}`}>
                      {it.name} <span className="ml-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600">Catch Weight</span>
                    </button>
                  ) : it.name}
                  {it.status === "inactive" && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600" data-testid={`item-inactive-badge-${it.id}`}>Nonaktif</span>}
                </TableCell>
                <TableCell className="text-xs font-mono text-slate-500" data-testid={`item-code-${it.id}`}>{it.code || "-"}</TableCell>
                <TableCell className="text-slate-500">{it.category || "-"}</TableCell>
                <TableCell>{isCw ? "Roll / KG" : it.unit}</TableCell>
                <TableCell className="text-right font-bold" data-testid={`item-stock-${it.id}`}>
                  {isCw ? <CwStockCell summary={cwSummaries[it.id]} rollQty={it.on_hand} /> : numberID(it.on_hand)}
                </TableCell>
                <TableCell className="text-right text-slate-400">{isCw ? <CwMinimumCell item={it} /> : numberID(it.minimum_stock || 0)}</TableCell>
                <TableCell><StatusBadge status={st} label={STATUS_LABEL[st]} /></TableCell>
                <TableCell className="text-xs text-slate-400">{it.location || "-"}</TableCell>
                {showActions && <TableCell className="text-right"><div className="flex justify-end gap-1">
                  {canTransact && <Button variant="ghost" size="sm" title="Barang Masuk" onClick={() => onMove({ type: "IN", item: it })} data-testid={`in-btn-${it.id}`}><PackagePlus className="h-4 w-4 text-emerald-600" /></Button>}
                  {canTransact && <Button variant="ghost" size="sm" title="Barang Keluar" onClick={() => onMove({ type: "OUT", item: it })} data-testid={`out-btn-${it.id}`}><PackageMinus className="h-4 w-4 text-rose-600" /></Button>}
                  {canTransact && <Button variant="ghost" size="sm" title="Ubah Item" onClick={() => onEdit(it)} data-testid={`edit-btn-${it.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                  {canDelete && <Button variant="ghost" size="sm" title="Hapus / Nonaktifkan" onClick={() => onDelete(it)} data-testid={`delete-btn-${it.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                </div></TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      )}
      </div>
    </div>
  );
}

function MinimumTab({ items, canTransact, onEdit }) {
  if (!items) return <Loading />;
  const active = items.filter((it) => it.status !== "inactive");
  if (active.length === 0) return <EmptyState title="Belum ada item" subtitle="Tambahkan item untuk mengatur minimum stok." icon={Boxes} />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Nama Item</TableHead><TableHead>Kategori</TableHead><TableHead>Satuan</TableHead>
          <TableHead className="text-right">Stok Saat Ini</TableHead><TableHead className="text-right">Minimum Stok</TableHead>
          <TableHead>Status</TableHead>{canTransact && <TableHead className="text-right">Aksi</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {active.map((it) => {
            const min = it.minimum_stock || 0;
            const st = itemStatus(it.on_hand, min);
            return (
              <TableRow key={it.id} data-testid={`min-row-${it.id}`}>
                <TableCell className="font-medium">{it.name}</TableCell>
                <TableCell className="text-slate-500">{it.category || "-"}</TableCell>
                <TableCell>{it.unit}</TableCell>
                <TableCell className="text-right font-bold">{numberID(it.on_hand)} <span className="text-xs font-normal text-slate-400">{it.unit}</span></TableCell>
                <TableCell className="text-right">{min > 0 ? <span>{numberID(min)} <span className="text-xs text-slate-400">{it.unit}</span></span> : <span className="text-xs text-slate-300">belum diatur</span>}</TableCell>
                <TableCell><StatusBadge status={st} label={STATUS_LABEL[st]} /></TableCell>
                {canTransact && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => onEdit(it)} data-testid={`set-min-${it.id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Atur</Button></TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

const PAGE_SIZE = 25;
function RiwayatTab({ wh, items, meta }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [users, setUsers] = useState([]);
  const [tick, setTick] = useState(0);
  const [f, setF] = useState({ date_from: "", date_to: "", item_id: "all", category: "all", type: "all", created_by_name: "all", q: "" });

  useEffect(() => { if (wh) api.get("/inventory/transactions/users", { params: { warehouse_id: wh.id } }).then((r) => setUsers(r.data)).catch(() => {}); }, [wh]);

  const buildParams = () => {
    const p = { warehouse_id: wh.id, skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (f.date_from) p.date_from = f.date_from;
    if (f.date_to) p.date_to = f.date_to;
    if (f.item_id !== "all") p.item_id = f.item_id;
    if (f.category !== "all") p.category = f.category;
    if (f.type !== "all") p.type = f.type;
    if (f.created_by_name !== "all") p.created_by_name = f.created_by_name;
    if (f.q.trim()) p.q = f.q.trim();
    return p;
  };

  useEffect(() => {
    if (!wh) return;
    setRows(null);
    const p = buildParams();
    api.get("/inventory/transactions", { params: p }).then((r) => setRows(r.data)).catch((e) => { setRows([]); toast.error(apiError(e)); });
    api.get("/inventory/transactions/count", { params: { ...p, skip: undefined, limit: undefined } }).then((r) => setTotal(r.data.total)).catch(() => {});
  }, [wh, page, f.date_from, f.date_to, f.item_id, f.category, f.type, f.created_by_name, tick]);

  const applySearch = () => { setPage(0); setTick((t) => t + 1); };
  const resetFilters = () => { setPage(0); setTick((t) => t + 1); setF({ date_from: "", date_to: "", item_id: "all", category: "all", type: "all", created_by_name: "all", q: "" }); };
  const set = (k, v) => { setPage(0); setF((s) => ({ ...s, [k]: v })); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div><Label className="text-xs text-slate-500">Dari Tanggal</Label><Input type="date" className="mt-1" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" /></div>
          <div><Label className="text-xs text-slate-500">Sampai Tanggal</Label><Input type="date" className="mt-1" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" /></div>
          <div><Label className="text-xs text-slate-500">Nama Item</Label>
            <Select value={f.item_id} onValueChange={(v) => set("item_id", v)}><SelectTrigger className="mt-1" data-testid="filter-item"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Item</SelectItem>{(items || []).map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs text-slate-500">Kategori</Label>
            <Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger className="mt-1" data-testid="filter-category"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Kategori</SelectItem>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs text-slate-500">Jenis Transaksi</Label>
            <Select value={f.type} onValueChange={(v) => set("type", v)}><SelectTrigger className="mt-1" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Jenis</SelectItem>{TXN_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs text-slate-500">User / Dibuat Oleh</Label>
            <Select value={f.created_by_name} onValueChange={(v) => set("created_by_name", v)}><SelectTrigger className="mt-1" data-testid="filter-user"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua User</SelectItem>{users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1"><Label className="text-xs text-slate-500">Pencarian Item</Label>
            <Input className="mt-1" placeholder="Cari nama item..." value={f.q} onChange={(e) => setF((s) => ({ ...s, q: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && applySearch()} data-testid="filter-search" /></div>
          <Button variant="outline" onClick={applySearch} data-testid="apply-search-btn"><Search className="mr-1.5 h-4 w-4" /> Cari</Button>
          <Button variant="ghost" onClick={resetFilters} data-testid="reset-filter-btn"><X className="mr-1.5 h-4 w-4" /> Reset</Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Tidak ada riwayat" subtitle="Belum ada transaksi sesuai filter." icon={Boxes} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Tanggal & Waktu</TableHead><TableHead>Transaction ID</TableHead><TableHead>Nama Item</TableHead>
              <TableHead>Kategori</TableHead><TableHead>Jenis</TableHead>
              <TableHead className="text-right">Masuk</TableHead><TableHead className="text-right">Keluar</TableHead><TableHead>Satuan</TableHead>
              <TableHead className="text-right">Sebelum</TableHead><TableHead className="text-right">Sesudah</TableHead>
              <TableHead>Referensi</TableHead><TableHead>Catatan</TableHead><TableHead>Oleh</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} data-testid={`txn-row-${t.id}`}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(t.created_at)}</TableCell>
                  <TableCell className="font-mono text-[11px] text-slate-400">{t.id.slice(0, 8).toUpperCase()}</TableCell>
                  <TableCell className="font-medium">{t.item_name}</TableCell>
                  <TableCell className="text-slate-500">{t.category || "-"}</TableCell>
                  <TableCell><StatusBadge status={t.qty >= 0 ? "OK" : "OUT"} label={TXN_LABEL[t.type] || t.type} /></TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">{t.qty > 0 ? numberID(t.qty) : ""}</TableCell>
                  <TableCell className="text-right font-semibold text-rose-600">{t.qty < 0 ? numberID(Math.abs(t.qty)) : ""}</TableCell>
                  <TableCell className="text-xs text-slate-400">{t.unit}</TableCell>
                  <TableCell className="text-right text-slate-400">{numberID(t.qty_before)}</TableCell>
                  <TableCell className="text-right font-medium">{numberID(t.qty_after)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{typeof t.reference === "string" ? t.reference : (t.reference?.issue_number || t.reference?.job_number || "-")}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[160px] truncate">{t.notes || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{t.created_by_name || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-slate-500" data-testid="txn-total">Menampilkan {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} dari {numberID(total)} transaksi</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="prev-page">Sebelumnya</Button>
            <span className="text-xs text-slate-500">Hal {page + 1} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">Berikutnya</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemDialog({ wh, categories, units = [], item, onClose, onSaved, cwEnabled }) {
  const isEdit = !!item;
  const catList = item?.category && !categories.includes(item.category) ? [item.category, ...categories] : categories;
  const unitNames = units.map((u) => u.name);
  const initUnit = item?.unit || unitNames[0] || "Pcs";
  const [form, setForm] = useState({
    name: item?.name || "", code: item?.code || "", default_tracking_mode: item?.default_tracking_mode || "none",
    category: item?.category || categories[0], unit: initUnit,
    minimum_stock: item?.minimum_stock ?? 0, location: item?.location || "", supplier: item?.supplier || "",
    notes: item?.notes || "", status: item?.status || "active",
  });
  const codeLocked = isEdit && !!item?.code && (item?.on_hand > 0 || item?.reserved > 0);
  const [useCw, setUseCw] = useState(item?.tracking_mode === "CATCH_WEIGHT_ROLL");
  const cwLocked = isEdit && item?.tracking_mode === "CATCH_WEIGHT_ROLL";
  const cwEligible = cwEnabled && wh?.type === "KONVEKSI" && form.category === "Kain";
  const unitOptions = !form.unit || unitNames.includes(form.unit) ? unitNames : [form.unit, ...unitNames];
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name) return toast.error("Nama item wajib diisi");
    if (Number(form.minimum_stock) < 0) return toast.error("Minimum stok tidak boleh negatif");
    setSaving(true);
    try {
      const { default_tracking_mode, code, ...rest } = form;
      const useCatchWeight = cwEligible && useCw;
      const payload = {
        ...rest,
        minimum_stock: Number(form.minimum_stock),
        code: code?.trim() ? code.trim().toUpperCase() : null,
        default_tracking_mode: useCatchWeight ? null : (default_tracking_mode === "none" ? null : default_tracking_mode),
      };
      if (useCatchWeight) { payload.tracking_mode = "CATCH_WEIGHT_ROLL"; payload.unit = "Roll"; payload.category = "Kain"; }
      if (isEdit) await api.patch(`/inventory/items/${item.id}`, payload);
      else await api.post("/inventory/items", { warehouse_id: wh.id, ...payload });
      toast.success(isEdit ? "Item diperbarui" : "Item ditambahkan"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Ubah Item" : "Item Baru"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label className="text-xs text-slate-500">Nama Item</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" /></div>
          <div><Label className="text-xs text-slate-500">Kode Item (opsional)</Label><Input className="mt-1 uppercase" value={form.code} disabled={codeLocked} placeholder="mis. DP-DTF-INK-BLACK-INN" onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="item-code" />{codeLocked && <p className="mt-1 text-[10px] text-amber-600">Kode terkunci — item sudah memiliki transaksi.</p>}</div>
          <div><Label className="text-xs text-slate-500">Kategori</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1" data-testid="item-category"><SelectValue /></SelectTrigger>
              <SelectContent>{catList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          {cwEligible && (
            <div className="col-span-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3" data-testid="item-cw-section">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <input type="checkbox" checked={useCw} disabled={cwLocked} onChange={(e) => setUseCw(e.target.checked)} data-testid="item-cw-toggle" />
                Gunakan Fabric Catch Weight
              </label>
              {useCw && <p className="mt-2 text-xs text-indigo-700" data-testid="item-cw-info">Stok awal harus 0. Roll fisik akan dibuat saat Penerimaan Barang (GR). Harga beli & berat dalam KG; satuan utama ROLL.</p>}
              {cwLocked && <p className="mt-1 text-[10px] text-amber-600">Item sudah Catch Weight — mode tidak dapat diubah.</p>}
            </div>
          )}
          {!(cwEligible && useCw) && (
          <div><Label className="text-xs text-slate-500">Mode Tracking Default</Label>
            <Select value={form.default_tracking_mode} onValueChange={(v) => setForm({ ...form, default_tracking_mode: v })}><SelectTrigger className="mt-1" data-testid="item-tracking-mode"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">— (tidak diset)</SelectItem><SelectItem value="MEASURED">MEASURED</SelectItem><SelectItem value="ISSUE_ONLY">ISSUE_ONLY</SelectItem></SelectContent></Select>
          </div>
          )}
          {(cwEligible && useCw)
            ? <div><Label className="text-xs text-slate-500">Satuan</Label><Input className="mt-1 bg-slate-100" value="Roll (otomatis)" disabled data-testid="item-unit-cw" /></div>
            : <div><Label className="text-xs text-slate-500">Satuan</Label>
            <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}><SelectTrigger className="mt-1" data-testid="item-unit"><SelectValue placeholder="Pilih satuan" /></SelectTrigger>
              <SelectContent>{unitOptions.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
          </div>}
          <div><Label className="text-xs text-slate-500">Minimum Stok</Label><Input type="number" min="0" className="mt-1" value={form.minimum_stock} onChange={(e) => setForm({ ...form, minimum_stock: e.target.value })} data-testid="item-min-stock" /></div>
          <div><Label className="text-xs text-slate-500">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="mt-1" data-testid="item-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent></Select>
          </div>
          <div><Label className="text-xs text-slate-500">Lokasi (opsional)</Label><Input className="mt-1" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="item-location" /></div>
          <div><Label className="text-xs text-slate-500">Supplier (opsional)</Label><Input className="mt-1" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="item-supplier" /></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Catatan</Label><Textarea className="mt-1" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="item-notes" /></div>
        </div>
        {isEdit && <p className="text-[11px] text-slate-400">Stok tidak dapat diubah langsung — gunakan Barang Masuk/Keluar, Penyesuaian, atau Stock Opname.</p>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-item-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({ move, wh, onClose, onSaved }) {
  const [form, setForm] = useState({ qty: 0, reference: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (Number(form.qty) <= 0) return toast.error("Qty harus > 0");
    setSaving(true);
    try {
      const url = move.type === "IN" ? "/inventory/barang-masuk" : "/inventory/barang-keluar";
      await api.post(url, { warehouse_id: wh.id, ref_type: "item", ref_id: move.item.id, qty: Number(form.qty), reference: form.reference, notes: form.notes });
      toast.success(move.type === "IN" ? "Barang masuk dicatat" : "Barang keluar dicatat"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{move.type === "IN" ? "Barang Masuk" : "Barang Keluar"} — {move.item.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-500">Stok saat ini: <span className="font-bold text-slate-700">{numberID(move.item.on_hand)} {move.item.unit}</span></p>
          <div><Label className="text-xs text-slate-500">Jumlah ({move.item.unit})</Label><Input type="number" min="0" className="mt-1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="mat-move-qty" /></div>
          <div><Label className="text-xs text-slate-500">Referensi (opsional)</Label><Input className="mt-1" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="No. PO / dokumen" data-testid="mat-move-ref" /></div>
          <div><Label className="text-xs text-slate-500">Catatan</Label><Input className="mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="mat-move-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-mat-move-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FinishedGoodsTab({ wh, canTransact }) {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(null); // null | {variant_id?}
  const [f, setF] = useState({ q: "", fabric: "all", age: "all", sleeve: "all", color: "all", size: "all" });
  const load = () => {
    if (!wh) return;
    setRows(null);
    api.get("/inventory/balances", { params: { warehouse_id: wh.id, ref_type: "variant" } }).then((r) => setRows(r.data));
    api.get("/inventory/finished-goods-summary", { params: { warehouse_id: wh.id } }).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(load, [wh]);
  const distinct = (key) => Array.from(new Set((rows || []).map((r) => r.variant?.[key]).filter(Boolean)));
  const filtered = (rows || []).filter((r) =>
    (!f.q || r.sku.toLowerCase().includes(f.q.toLowerCase())) &&
    (f.fabric === "all" || r.variant?.fabric_name === f.fabric) &&
    (f.age === "all" || r.variant?.age_name === f.age) &&
    (f.sleeve === "all" || r.variant?.sleeve_name === f.sleeve) &&
    (f.color === "all" || r.variant?.color_name === f.color) &&
    (f.size === "all" || r.variant?.size_name === f.size));

  const Filt = ({ k, label, opts }) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}><SelectTrigger className="h-9 w-[130px]" data-testid={`fg-filter-${k}`}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent><SelectItem value="all">{label}: Semua</SelectItem>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="font-display text-base font-semibold text-slate-700">Barang Jadi Produksi <span className="text-xs font-normal text-slate-400">· staging sebelum transfer ke Inventory Pusat (tidak dijual online)</span></h3>
        {canTransact && <Button onClick={() => setOpen({})} data-testid="barang-jadi-masuk-btn" className="bg-indigo-600 hover:bg-indigo-500"><PackagePlus className="mr-1.5 h-4 w-4" /> Barang Jadi Masuk</Button>}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Jumlah SKU Barang Jadi" value={numberID(summary?.sku_count)} icon={Shirt} accent="indigo" testId="fg-sku-count" />
        <StatCard label="Total Pcs Barang Jadi" value={numberID(summary?.total_pcs)} icon={Boxes} accent="emerald" testId="fg-total-pcs" />
        <StatCard label="Menunggu Transfer" value={numberID(summary?.pending_transfer)} icon={Send} accent="amber" testId="fg-pending" />
        <StatCard label="Transfer Hari Ini" value={numberID(summary?.transferred_today)} icon={Send} accent="sky" testId="fg-today" />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]"><Label className="text-xs text-slate-500">Cari SKU</Label><Input className="mt-1 h-9" placeholder="C30-A-SS-BLK-L" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="fg-search" /></div>
        <Filt k="fabric" label="Bahan" opts={distinct("fabric_name")} />
        <Filt k="age" label="Umur" opts={distinct("age_name")} />
        <Filt k="sleeve" label="Lengan" opts={distinct("sleeve_name")} />
        <Filt k="color" label="Warna" opts={distinct("color_name")} />
        <Filt k="size" label="Ukuran" opts={distinct("size_name")} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : filtered.length === 0 ? <EmptyState title="Belum ada barang jadi" subtitle="Catat hasil produksi via 'Barang Jadi Masuk'." icon={Shirt} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>SKU</TableHead><TableHead>Bahan</TableHead><TableHead>Umur</TableHead><TableHead>Lengan</TableHead>
              <TableHead>Warna</TableHead><TableHead>Ukuran</TableHead>
              <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Reservasi</TableHead><TableHead className="text-right">Tersedia</TableHead>
              <TableHead>Update Terakhir</TableHead><TableHead>Status</TableHead>{canTransact && <TableHead className="text-right">Aksi</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const st = r.available > 0 ? "OK" : "OUT";
                return (
                  <TableRow key={r.id} data-testid={`fg-row-${r.sku}`}>
                    <TableCell className="font-mono text-xs font-semibold text-indigo-700">{r.sku}</TableCell>
                    <TableCell className="text-slate-500">{r.variant?.fabric_name}</TableCell>
                    <TableCell className="text-slate-500">{r.variant?.age_name}</TableCell>
                    <TableCell className="text-slate-500">{r.variant?.sleeve_name}</TableCell>
                    <TableCell className="text-slate-500">{r.variant?.color_name}</TableCell>
                    <TableCell className="text-slate-500">{r.variant?.size_name}</TableCell>
                    <TableCell className="text-right font-bold">{numberID(r.on_hand)}</TableCell>
                    <TableCell className="text-right text-amber-600">{numberID(r.reserved)}</TableCell>
                    <TableCell className="text-right font-semibold">{numberID(r.available)}</TableCell>
                    <TableCell className="text-xs text-slate-400">{formatDateTime(r.updated_at)}</TableCell>
                    <TableCell><StatusBadge status={st} label={st === "OK" ? "Siap Transfer" : "Kosong"} /></TableCell>
                    {canTransact && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => setOpen({ variant_id: r.ref_id })} data-testid={`fg-add-${r.sku}`}><PackagePlus className="h-4 w-4 text-emerald-600" /></Button></TableCell>}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {open && wh && <BarangJadiDialog wh={wh} presetVariant={open.variant_id} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function BarangJadiDialog({ wh, presetVariant, onClose, onSaved }) {
  const today = wibToday();
  const [variants, setVariants] = useState([]);
  const [form, setForm] = useState({ variant_id: presetVariant || "", qty: 0, tanggal: today, production_ref: "", qc_ref: "", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/products/variants", { params: { status: "active", limit: 1000 } }).then((r) => setVariants(r.data.items)); }, []);
  const save = async () => {
    if (!form.variant_id) return toast.error("Pilih SKU barang jadi");
    if (Number(form.qty) <= 0) return toast.error("Jumlah harus > 0");
    setSaving(true);
    try {
      await api.post("/inventory/barang-jadi-masuk", { warehouse_id: wh.id, ...form, qty: Number(form.qty) });
      toast.success("Barang jadi produksi dicatat"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Barang Jadi Masuk (Produksi)</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label className="text-xs text-slate-500">SKU (Global Product Variant)</Label>
            <Select value={form.variant_id} onValueChange={(v) => setForm({ ...form, variant_id: v })}><SelectTrigger className="mt-1" data-testid="bj-variant"><SelectValue placeholder="Pilih SKU..." /></SelectTrigger>
              <SelectContent>{variants.map((v) => <SelectItem key={v.id} value={v.id}><span className="font-mono text-xs">{v.sku}</span></SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs text-slate-500">Tanggal</Label><Input type="date" className="mt-1" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} data-testid="bj-tanggal" /></div>
          <div><Label className="text-xs text-slate-500">Jumlah (Pcs)</Label><Input type="number" min="0" className="mt-1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="bj-qty" /></div>
          <div><Label className="text-xs text-slate-500">Referensi Produksi</Label><Input className="mt-1" value={form.production_ref} onChange={(e) => setForm({ ...form, production_ref: e.target.value })} data-testid="bj-prod-ref" /></div>
          <div><Label className="text-xs text-slate-500">Referensi QC</Label><Input className="mt-1" value={form.qc_ref} onChange={(e) => setForm({ ...form, qc_ref: e.target.value })} data-testid="bj-qc-ref" /></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Catatan</Label><Textarea className="mt-1" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="bj-notes" /></div>
        </div>
        <p className="text-[11px] text-slate-400">Stok masuk ke staging Barang Jadi Produksi (Konveksi). Belum dapat dijual online sampai ditransfer & diterima di Inventory Pusat.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-bj-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const COVERAGE_META = {
  lengkap: { label: "Lengkap", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  sebagian: { label: "Sebagian", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  tidak_dapat_dihitung: { label: "Tidak Dapat Dihitung", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  belum_tersedia: { label: "Belum Tersedia", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const SOURCE_LABEL = {
  LAST_PURCHASE_PRICE: "Harga Beli Terakhir", AVERAGE_COST: "Average Cost",
  OPENING_COST: "Opening Cost", REFERENCE_PRICE: "Harga Referensi", UNAVAILABLE: "Belum Tersedia",
};
const perUnit = (v, basis) => (v == null ? "—" : `${rupiah(v)}${basis ? " / " + basis : ""}`);
const estValue = (c) =>
  c.estimated_value != null ? rupiah(c.estimated_value)
    : c.coverage_class === "tidak_dapat_dihitung" ? "Belum dapat dihitung" : "Belum tersedia";

function CoverageBadge({ cls }) {
  const m = COVERAGE_META[cls] || COVERAGE_META.belum_tersedia;
  return <Badge variant="outline" className={m.cls}>{m.label}</Badge>;
}

function CostTab({ wh, canManage }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const load = () => {
    if (!wh) return;
    setRows(null);
    api.get("/inventory/items-cost", { params: { warehouse_id: wh.id } })
      .then((r) => setRows(r.data.filter((x) => x.status !== "inactive"))).catch(() => setRows([]));
  };
  useEffect(load, [wh]);
  if (!rows) return <Loading />;
  if (rows.length === 0) return <EmptyState title="Belum ada item" subtitle="Tambahkan item pada tab Stok Bahan." icon={Coins} />;
  const reliable = rows.filter((r) => r.cost.cost_coverage === "reliable").length;
  const notPriced = rows.length - reliable;
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between" data-testid="cost-summary">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Coins className="h-4 w-4 text-emerald-600" /> Cost &amp; Estimasi Nilai Persediaan</p>
          <p className="mt-0.5 text-xs text-slate-500" data-testid="cost-coverage-summary">{reliable} dari {rows.length} item memiliki cost reliable · {notPriced} item belum dapat dinilai</p>
        </div>
        <p className="text-[11px] italic text-slate-400">Estimasi — bukan nilai accounting resmi</p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nama Item</TableHead><TableHead>Satuan Stok</TableHead><TableHead>Cost Basis</TableHead>
            <TableHead className="text-right">Harga Referensi</TableHead><TableHead className="text-right">Opening Cost</TableHead>
            <TableHead className="text-right">Harga Beli Terakhir</TableHead><TableHead className="text-right">Average Cost</TableHead>
            <TableHead className="text-right">Estimasi Nilai</TableHead><TableHead>Cost Source</TableHead>
            <TableHead>Cost Coverage</TableHead><TableHead className="text-right">Detail</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => {
              const c = r.cost; const basis = c.cost_basis || r.unit;
              return (
                <TableRow key={r.id} data-testid={`cost-row-${r.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setSel(r)}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-slate-500">{r.unit || "-"}</TableCell>
                  <TableCell className="text-slate-500">{basis || "-"}</TableCell>
                  <TableCell className="text-right">{perUnit(c.reference_price, basis)}</TableCell>
                  <TableCell className="text-right">{perUnit(c.opening_cost, basis)}</TableCell>
                  <TableCell className="text-right">{perUnit(c.last_purchase_price, basis)}</TableCell>
                  <TableCell className="text-right">{perUnit(c.average_cost, basis)}</TableCell>
                  <TableCell className="text-right font-semibold">{estValue(c)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{SOURCE_LABEL[c.cost_source] || c.cost_source}</TableCell>
                  <TableCell><CoverageBadge cls={c.coverage_class} /></TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm" data-testid={`cost-detail-${r.id}`}><Info className="h-4 w-4 text-slate-500" /></Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {sel && <CostDrawer item={sel} canManage={canManage} onClose={() => setSel(null)} onSaved={() => { setSel(null); load(); }} />}
    </div>
  );
}

function CostDrawer({ item, canManage, onClose, onSaved }) {
  const c = item.cost;
  const basis = c.cost_basis || item.unit;
  const [ref, setRef] = useState(c.reference_price ?? "");
  const [saving, setSaving] = useState(false);
  const saveRef = async () => {
    setSaving(true);
    try {
      await api.put(`/inventory/items/${item.id}/cost`, { reference_price: ref === "" ? null : Number(ref) });
      toast.success("Harga Referensi diperbarui"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const Row = ({ label, value, hint }) => (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5">
      <div><p className="text-xs text-slate-500">{label}</p>{hint && <p className="text-[11px] text-slate-400">{hint}</p>}</div>
      <p className="text-right text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="cost-drawer">
        <SheetHeader><SheetTitle className="font-display">{item.name}</SheetTitle></SheetHeader>
        <div className="mt-4">
          <div className="mb-3 flex items-center gap-2"><CoverageBadge cls={c.coverage_class} /><span className="text-xs text-slate-500">Sumber: {SOURCE_LABEL[c.cost_source] || c.cost_source}</span></div>
          <Row label="Satuan Stok" value={item.unit || "-"} />
          <Row label="Stok Saat Ini (On Hand)" value={`${numberID(c.on_hand)} ${item.unit || ""}`} />
          <Row label="Cost Basis / Satuan Harga" value={basis || "-"} />
          <Row label="Qty Basis Cost" value={c.cost_basis_qty != null ? `${numberID(c.cost_basis_qty)} ${basis || ""}` : "—"} hint={basis && item.unit && basis !== item.unit ? "Diperlukan untuk hitung nilai" : null} />
          <Row label="Opening Cost" value={perUnit(c.opening_cost, basis)} hint="Read-only · hanya via Saldo Awal Inventory" />
          <Row label="Harga Beli Terakhir" value={perUnit(c.last_purchase_price, basis)} hint="Read-only · dari sistem (Goods Receipt)" />
          <Row label="Average Cost" value={perUnit(c.average_cost, basis)} hint="Read-only · dihitung sistem" />
          <Row label="Estimasi Nilai Persediaan" value={estValue(c)} hint="Estimasi — bukan nilai accounting resmi" />
          <div className="mt-5">
            <Label className="text-xs text-slate-500">Harga Referensi {basis ? `(/ ${basis})` : ""}</Label>
            {canManage ? (
              <div className="mt-1 flex gap-2">
                <Input type="number" min="0" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="cost-ref-input" placeholder="cth. 92500" />
                <Button onClick={saveRef} disabled={saving} data-testid="cost-ref-save" className="bg-indigo-600 hover:bg-indigo-500"><Save className="mr-1 h-4 w-4" /> {saving ? "..." : "Simpan"}</Button>
              </div>
            ) : (
              <p className="mt-1 text-sm font-semibold text-slate-800">{perUnit(c.reference_price, basis)}</p>
            )}
            <p className="mt-1 text-[11px] text-slate-400">Hanya Harga Referensi yang dapat diedit. Opening Cost, Harga Beli Terakhir, dan Average Cost bersifat read-only.</p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
